/**
 * Wie groß die Schrift ist — und was der Bildschirm daraus macht.
 *
 * **Sie hängt am Konto, nicht am Gerät.** Hier stand einmal das Gegenteil:
 * „im Browser abgelegt und nicht auf dem Server — es geht auch niemanden
 * sonst etwas an." Der zweite Teil stimmt weiterhin, der erste war
 * unpraktisch: wer am iPad groß stellte, saß am iPhone wieder klein. Aus dem
 * Betrieb gewünscht, dass sie mitgeht.
 *
 * Sie liegt deshalb an `users.font_scale` und kommt mit `/api/auth/me`. **Im
 * Browser bleibt eine Kopie**, und die ist kein Rückschritt, sondern der
 * Grund, warum die Seite nicht erst grau aufblitzt: sie gilt in der
 * Millisekunde des Starts, bevor die erste Antwort da ist — und sie trägt
 * auch, wenn gar kein Netz da ist.
 *
 * **Eine Verwaltungseinstellung ist es nicht.** Sie sagt etwas über die Augen
 * des Menschen vor dem Schirm; jeder setzt sie für sich, auch wer nur
 * mitliest.
 *
 * **Die Stufe sitzt an der Wurzel und gilt für alles.** Sie setzt die
 * Schriftgröße von `:root` in Prozent; jede Größe der Oberfläche steht in
 * `rem` und folgt daraus. Vorher bewegte sie nur die Sprechblasen — eine
 * Einstellung, die „Schriftgröße" heißt und den Rest so klein lässt, wie er
 * war, ist eine halbe Auskunft.
 *
 * **Im Chat skaliert sie zusätzlich mit dem Bildschirm.** Dort kommt die
 * Größe aus `clamp()` mit einem `vw`-Anteil: auf einem iPhone SE steht etwas
 * anderes da als auf einem 27-Zoll-Schirm, ohne dass jemand nachstellt — und
 * die Grenzen verhindern, dass es auf einem Fernseher albern wird.
 *
 * **`klein` ist der Stand von früher** und nicht kleiner: die Eingabefelder
 * hängen an `1rem` und dürfen 16 Punkte nicht unterschreiten, sonst zoomt
 * iOS beim Antippen hinein.
 */
export type Schriftgroesse = 'klein' | 'mittel' | 'gross'

/*
 * Die Reihenfolge der Stufen -- **ohne ihre Namen** (1.64.0). Die standen hier
 * als zweite Spalte, und das war Oberflächentext in einer Datei ohne
 * Komponente. Wie eine Stufe heißt, steht in `texte/de.ts` unter
 * `schrift.stufen`; die Karte schlägt es über den Wert nach.
 */
export const SCHRIFTGROESSEN: Schriftgroesse[] = ['klein', 'mittel', 'gross']

const SCHLUESSEL = 'tb.schrift'

export function gespeicherteGroesse(): Schriftgroesse {
  try {
    const wert = localStorage.getItem(SCHLUESSEL)
    if (wert === 'klein' || wert === 'mittel' || wert === 'gross') return wert
  } catch {
    // Privates Fenster, Speicher voll: dann eben die Vorgabe.
  }
  return 'mittel'
}

/**
 * Die Größe setzen — als Attribut am Wurzelelement.
 *
 * Ein Attribut und keine Klasse, weil es genau einen Wert gibt und nicht
 * mehrere gleichzeitig: `data-schrift="gross"` kann nicht versehentlich
 * zweimal dastehen, zwei Klassen schon.
 */
export function setzeGroesse(wert: Schriftgroesse): void {
  document.documentElement.setAttribute('data-schrift', wert)
  try {
    localStorage.setItem(SCHLUESSEL, wert)
  } catch {
    // Nicht speichern zu können heißt: es gilt bis zum Neuladen. Kein Fehler.
  }
}

/**
 * Was vom Server kommt, gilt — und wird als Kopie hinterlegt.
 *
 * Aufgerufen, sobald `/api/auth/me` da ist. Ein unbekannter Wert wird
 * stillschweigend übergangen: eine ältere Oberfläche gegen einen neueren
 * Server soll nicht an einer Zeichenkette scheitern, die sie nicht kennt.
 */
export function vomKonto(wert: string | undefined): void {
  if (wert !== 'klein' && wert !== 'mittel' && wert !== 'gross') return
  if (wert === gespeicherteGroesse() && document.documentElement.getAttribute('data-schrift') === wert) {
    return
  }
  setzeGroesse(wert)
}

/** Beim Start einmal anwenden, bevor der erste Chat gezeichnet wird. */
export function schriftgroesseAnwenden(): void {
  document.documentElement.setAttribute('data-schrift', gespeicherteGroesse())
}
